import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { Save, RotateCcw, Copy, Download, FileText, AlertCircle, X } from 'lucide-react';
import api from '../utils/axios';
import { FileNode } from './FileExplorer';
import { IconName } from './Icon/Icon.type';
import { Button, Text, Modal, Icon } from './index';
import { useTheme } from '../contexts/ThemeContext';

interface CodeEditorProps {
  file: FileNode | null;
  projectId: string;
  onFileSave?: (file: FileNode, content: string) => void;
  onFileClose?: () => void;
  className?: string;
  readOnly?: boolean;
  minWidth?: number;
}

const getLanguageFromExtension = (fileName: string): string => {
  const lowerFileName = fileName.toLowerCase();
  
  // Handle files without extensions or special cases
  if (lowerFileName === 'dockerfile' || lowerFileName.startsWith('dockerfile.')) {
    return 'dockerfile';
  }
  if (lowerFileName === '.env' || lowerFileName.startsWith('.env.') || lowerFileName.endsWith('.env')) {
    return 'plaintext';
  }
  if (lowerFileName === '.gitignore' || lowerFileName === 'gitignore') {
    return 'plaintext';
  }
  
  const extension = lowerFileName.split('.').pop();
  
  const languageMap: { [key: string]: string } = {
    'js': 'javascript',
    'jsx': 'javascript',
    'ts': 'typescript',
    'tsx': 'typescript',
    'py': 'python',
    'java': 'java',
    'cpp': 'cpp',
    'c': 'c',
    'cs': 'csharp',
    'php': 'php',
    'rb': 'ruby',
    'go': 'go',
    'rs': 'rust',
    'html': 'html',
    'htm': 'html',
    'xml': 'xml',
    'css': 'css',
    'scss': 'scss',
    'sass': 'sass',
    'less': 'less',
    'json': 'json',
    'yaml': 'yaml',
    'yml': 'yaml',
    'toml': 'toml',
    'ini': 'ini',
    'conf': 'plaintext',
    'config': 'plaintext',
    'md': 'markdown',
    'txt': 'plaintext',
    'sql': 'sql',
    'sh': 'shell',
    'bash': 'shell',
    'zsh': 'shell',
    'bat': 'bat',
    'dockerfile': 'dockerfile',
    'gitignore': 'plaintext',
    'env': 'plaintext'
  };
  
  // Default to plaintext for any unknown extension - this allows viewing any file
  return languageMap[extension || ''] || 'plaintext';
};

const CodeEditor: React.FC<CodeEditorProps> = ({
  file,
  projectId,
  onFileSave,
  onFileClose,
  className = '',
  readOnly = false,
  minWidth
}) => {
  const { theme } = useTheme();
  const [content, setContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const editorRef = useRef<any>(null);

  const loadFileContent = async (filePath: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(
        `/dev-agent/projects/${projectId}/files/${encodeURIComponent(filePath)}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      const fileContent = response.data.content || '';
      setContent(fileContent);
      setOriginalContent(fileContent);
      setHasChanges(false);
      
    } catch (err: any) {
      console.error('Error loading file content:', err);
      setError(err.response?.data?.detail || 'Failed to load file content');
      setContent('');
      setOriginalContent('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (file && file.type === 'file') {
      loadFileContent(file.path);
    } else {
      setContent('');
      setOriginalContent('');
      setHasChanges(false);
    }
  }, [file, projectId]);

  useEffect(() => {
    setHasChanges(content !== originalContent);
  }, [content, originalContent]);

  const handleSave = async () => {
    if (!file || saving) return;
    
    try {
      setSaving(true);
      setError(null);
      
      await api.put(`/dev-agent/projects/${projectId}/files/${encodeURIComponent(file.path)}`, {
        content: content
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setOriginalContent(content);
      setHasChanges(false);
      onFileSave?.(file, content);
      
    } catch (err: any) {
      console.error('Error saving file:', err);
      setError(err.response?.data?.detail || 'Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    setContent(originalContent);
    setHasChanges(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
  };

  const handleDownload = () => {
    if (!file) return;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    onFileClose?.();
  };

  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    
    // Configure editor settings
    editor.updateOptions({
      fontSize: 14,
      tabSize: 2,
      insertSpaces: true,
      detectIndentation: true,
      folding: true,
      lineNumbers: 'on',
      minimap: { enabled: true },
      scrollBeyondLastLine: false,
      wordWrap: 'on',
      automaticLayout: true
    });

    // Add keyboard shortcuts
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      if (!readOnly && hasChanges) {
        handleSave();
      }
    });
  };

  // Main container styles matching Figma design
  // NOTE: Properties NOT in tailwind.config.js that need to remain as-is:
  // - minWidth: '450px' - No equivalent spacing token for 450px in design system
  // - h-[28px], w-[28px] - Specific button dimensions not in design system (used in Button className)
  // - h-[16px], w-[16px] - Specific icon dimensions not in design system (used in Icon className)
  // - px-1 - Very small spacing (4px) used for very-small button size, acceptable as standard Tailwind utility
  // - -mx-3 - Negative margin utility, acceptable as standard Tailwind utility
  // - h-4, w-4, h-8, w-8 - Standard Tailwind size utilities, acceptable per design system guidelines
  const containerStyle: React.CSSProperties = {
    minWidth: '450px',
  };

  if (!file) {
    return (
      <div 
        className={`flex flex-col items-start self-stretch bg-bg-primary border-r border-border-secondary flex-1 pb-0 rounded-l-none rounded-r-bg-v2-lg overflow-hidden ${className}`}
        style={containerStyle}
      >
        {/* Header with close button */}
        <div className="flex items-center justify-between w-full min-h-[60px] px-bg-v2-xl border-b border-border-primary">
          <div className="flex items-center gap-bg-v2-md flex-1 min-w-0">
            <Text variant="text-sm" weight="semibold" color="tertiary">
              No file selected
            </Text>
          </div>
          
          <div className="flex items-center gap-bg-v2-md shrink-0">
            <Button
              variant="ghost"
              size="very-small"
              onClick={handleClose}
              className="!rounded-full !w-8 !h-8 !p-0 flex items-center justify-center text-fg-secondary"
              label={<Icon name={IconName.Cross} className="w-[16px] h-[16px]" inheritColor />}
            />
          </div>
        </div>

        {/* Empty state content */}
        <div className="flex items-center justify-center h-full w-full flex-1">
          <div className="text-center text-text-tertiary">
            <FileText size={48} className="mx-auto mb-bg-v2-xl opacity-50" />
            <p className="text-bg-v2-lg font-bg-v2-medium">No file selected</p>
            <p className="text-bg-v2-sm">Select a file from the explorer to view its contents</p>
          </div>
        </div>
      </div>
    );
  }

  if (file.type === 'directory') {
    return (
      <div 
        className={`flex flex-col items-start self-stretch bg-bg-primary border-r border-border-secondary flex-1 pb-0 rounded-l-none rounded-r-bg-v2-lg overflow-hidden ${className}`}
        style={containerStyle}
      >
        {/* Header with close button */}
        <div className="flex items-center justify-between w-full min-h-[60px] px-bg-v2-xl border-b border-border-primary">
          <div className="flex items-center gap-bg-v2-md flex-1 min-w-0">
            <Text variant="text-sm" weight="semibold" color="primary" className="truncate">
              {file.name}
            </Text>
          </div>
          
          <div className="flex items-center gap-bg-v2-md shrink-0">
            <Button
              variant="ghost"
              size="very-small"
              onClick={handleClose}
              className="!rounded-full !w-8 !h-8 !p-0 flex items-center justify-center text-fg-secondary"
              label={<Icon name={IconName.Cross} className="w-[16px] h-[16px]" inheritColor />}
            />
          </div>
        </div>

        {/* Directory state content */}
        <div className="flex items-center justify-center h-full w-full flex-1">
          <div className="text-center text-text-tertiary">
            <FileText size={48} className="mx-auto mb-bg-v2-xl opacity-50 text-text-primary" />
            <p className="text-bg-v2-lg font-bg-v2-medium">Directory selected</p>
            <p className="text-bg-v2-sm">Please select a file to view its contents</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`flex flex-col items-start self-stretch bg-bg-primary border-r border-border-secondary flex-1 pb-0 rounded-l-none rounded-r-bg-v2-lg overflow-hidden ${className}`}
      style={containerStyle}
    >
      {/* Header */}
      <div className="flex items-center justify-between w-full min-h-[60px] px-bg-v2-xl border-b border-border-primary">
        <div className="flex items-center gap-bg-v2-md flex-1 min-w-0">
          <Text variant="text-sm" weight="semibold" color="primary" className="truncate">
            {file.name}
          </Text>
          {hasChanges && <span className="text-bg-v2-xs text-text-warning-primary shrink-0">● modified</span>}
        </div>
        
        <div className="flex items-center gap-bg-v2-md shrink-0">
          {error && (
            <div className="flex items-center gap-bg-v2-xxs text-text-error-primary">
              <AlertCircle size={14} />
              <span className="text-bg-v2-xs">{error}</span>
            </div>
          )}
          
          <Button
            variant="ghost"
            size="very-small"
            onClick={handleCopy}
            className="px-1 h-[28px] min-h-0 w-[28px] !rounded-full"
            label={<Icon name={IconName.Copy} className="w-[16px] h-[16px] text-fg-secondary" inheritColor />}
          />
          
          <Button
            variant="ghost"
            size="very-small"
            onClick={handleDownload}
            className="px-1 h-[28px] min-h-0 w-[28px] !rounded-full"
            label={<Icon name={IconName.Download} className="w-[16px] h-[16px] text-fg-secondary" inheritColor />}
          />
          
          {!readOnly && hasChanges && (
            <>
              <Button
                variant="ghost"
                size="very-small"
                onClick={handleRevert}
                className="px-1 h-[28px] min-h-0 w-[28px] !rounded-full"
                label={<RotateCcw size={14} className="text-fg-secondary" />}
              />
              
              <Button
                variant="primary"
                size="small"
                onClick={handleSave}
                disabled={saving}
                label={saving ? 'Saving...' : 'Save'}
              />
            </>
          )}
          
          <Button
            variant="ghost"
            size="very-small"
            onClick={handleClose}
            className="!rounded-full !w-6 !h-6 !p-0 flex items-center justify-center text-fg-secondary"
            label={<Icon name={IconName.Cross} size="sm" inheritColor />}
          />
        </div>
      </div>

      {/* Editor - Code snippet section touches bottom */}
      <div className="w-full flex-1 relative mb-0">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-bg-v2-md text-text-tertiary">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-fg-brand-primary"></div>
              <span className="text-bg-v2-sm">Loading file...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-text-error-primary">
              <AlertCircle size={48} className="mx-auto mb-bg-v2-xl" />
              <p className="text-bg-v2-lg font-bg-v2-medium">Error loading file</p>
              <p className="text-bg-v2-sm mt-bg-v2-xxs">{error}</p>
              <button
                onClick={() => loadFileContent(file.path)}
                className="mt-bg-v2-lg px-bg-v2-xl py-bg-v2-md bg-bg-brand-solid text-text-primary-on-brand rounded-bg-v2-md text-bg-v2-sm hover:bg-bg-brand-solid-hover"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <Editor
            height="100%"
            language={getLanguageFromExtension(file.name)}
            value={content}
            onChange={(value: string | undefined) => setContent(value || '')}
            onMount={handleEditorDidMount}
            theme={theme === 'dark' ? 'vs-dark' : 'vs-light'}
            options={{
              readOnly: readOnly,
              automaticLayout: true,
              scrollBeyondLastLine: false,
              fontSize: 14,
              lineHeight: 20,
              tabSize: 2,
              insertSpaces: true,
              detectIndentation: true,
              folding: true,
              lineNumbers: 'on',
              minimap: { enabled: false },
              wordWrap: 'on',
              bracketPairColorization: { enabled: true },
              guides: {
                indentation: true,
                bracketPairs: true
              }
            }}
            loading={
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fg-brand-primary"></div>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default CodeEditor;